/**
 * Pending Changes Modal Component
 * Shows before/after preview of pending changes from staging table before committing to production
 */

import React, { useState, useEffect } from 'react';
import { forecastAPI, driversAPI, driverPricesAPI } from '@/services/commentary.service';
import {apiClient} from '@rti/shared';
import { X, ChevronRight, AlertCircle, Loader2 } from 'lucide-react';

const asUtcTs = (s: string) => s.includes('Z') || s.includes('+') ? s : s.replace(' ', 'T') + 'Z';

interface PendingChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCommit: () => void;
  onEdit?: () => void;
  onCancelStaging?: () => Promise<void>;
  changeType: 'forecast' | 'drivers' | 'peacc' | 'ppacc' | 'psacc' | 'pvcacc' | 'daily';
  pendingData?: any[];
  commentaryType?: 'preliminary' | 'final';
  driversSubView?: 'commentary' | 'pricechanges';
}

interface PendingChange {
  forecastQtrDateId?: string;
  forecastQtr?: number;
  commentary?: string;
  polymer?: string;
  lastUpdatedBy?: string | null;
  lastUpdatedTime?: string | null;
  forecastYear?: number;
  driverRptUnqId?: string;
  date?: string;
  driverReport?: string;
  title?: string;
  marketOverviewBody?: string;
  marketOverviewBodyTrial?: string;
  supplyDemandTitle?: string;
  supplyDemandBody?: string;
  feedstocksBody?: string;
  internationalBody?: string;
  actionStrategies?: string;
}

interface FieldChange {
  field: string;
  label: string;
  before: string | number | null;
  after: string | number | null;
}

export const PendingChangesModal: React.FC<PendingChangesModalProps> = ({
  isOpen,
  onClose,
  onCommit,
  onEdit,
  onCancelStaging,
  changeType,
  pendingData = [],
  commentaryType = 'preliminary',
  driversSubView = 'commentary',
}) => {
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  const [publishedDataState, setPublishedDataState] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('Loading pending changes...');
  const [error, setError] = useState<string | null>(null);
  const [cancelConfirmVisible, setCancelConfirmVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (changeType === 'forecast' || changeType === 'drivers') {
        fetchAllData();
      } else if (changeType === 'daily') {
        const sortedPending = [...pendingData].sort((a, b) =>
          new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        setPendingChanges(sortedPending);
      } else {
        const sortedPending = [...pendingData].sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB.getTime() - dateA.getTime();
        });
        setPendingChanges(sortedPending);
        fetchCachedACCData();
      }
    }
  }, [isOpen, changeType, pendingData, driversSubView]);

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Checking for pending changes...');
    setError(null);
    try {
      let pending: any[] = [];
      let published: any[] = [];

      if (changeType === 'forecast') {
        pending = await forecastAPI.getPending();
        published = await forecastAPI.getAll();
      } else if (changeType === 'drivers') {
        if (driversSubView === 'pricechanges') {
          pending = await driverPricesAPI.getPending();
          published = await apiClient.fetchCachedDriverPriceChangesData() || [];
          if (Array.isArray(published) && published.length > 0 && typeof published[0] === 'object') {
            const firstItem = published[0];
            const DRIVERS = ['PE', 'PP', 'PS', 'ABS', 'PC', 'PVC', 'PA66', 'PA6', 'PET'];
            const flattenedPublished: any[] = [];
            DRIVERS.forEach(driver => {
              const driverData = firstItem[driver];
              if (Array.isArray(driverData)) {
                driverData.forEach((item: any) => {
                  flattenedPublished.push({
                    date: item.date,
                    driver: driver,
                    value: item.value
                  });
                });
              }
            });
            published = flattenedPublished;
          }
        } else {
          pending = await driversAPI.getPending();
          published = await driversAPI.getAll();
        }
      }

      pending.sort((a, b) => {
        const dateA = new Date(a.date || a.forecastQtrDateId);
        const dateB = new Date(b.date || b.forecastQtrDateId);
        return dateB.getTime() - dateA.getTime();
      });

      setPendingChanges(pending);
      setPublishedDataState(published);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCachedACCData = async () => {
    setLoading(true);
    setLoadingMessage('Loading ACC data...');
    setError(null);
    try {
      let cached: any[] | null = null;

      if (changeType === 'peacc') {
        cached = commentaryType === 'final'
          ? await apiClient.fetchCachedACCPEDataFinal()
          : await apiClient.fetchCachedACCPEData();
      } else if (changeType === 'ppacc') {
        cached = commentaryType === 'final'
          ? await apiClient.fetchCachedACCPPDataFinal()
          : await apiClient.fetchCachedACCPPData();
      } else if (changeType === 'psacc') {
        cached = commentaryType === 'final'
          ? await apiClient.fetchCachedACCPSDataFinal()
          : await apiClient.fetchCachedACCPSData();
      } else if (changeType === 'pvcacc') {
        cached = commentaryType === 'final'
          ? await apiClient.fetchCachedACCPVCDataFinal()
          : await apiClient.fetchCachedACCPVCData();
      }

      setPublishedDataState(cached || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch cached ACC data');
    } finally {
      setLoading(false);
    }
  };

  const computeTextDiff = (oldText: string | null, newText: string | null): { oldHtml: string, newHtml: string } => {
    if (!oldText && !newText) {
      return { oldHtml: '', newHtml: '' };
    }

    if (!oldText) {
      return {
        oldHtml: '<div class="text-rs-off-white/40 text-sm italic">(empty)</div>',
        newHtml: `<div class="bg-rs-light-green/20 px-2 py-1 rounded">${newText || ''}</div>`
      };
    }

    if (!newText) {
      return {
        oldHtml: `<div class="bg-rs-red/20 px-2 py-1 rounded line-through">${oldText}</div>`,
        newHtml: '<div class="text-rs-off-white/40 text-sm italic">(empty)</div>'
      };
    }

    const stripHtml = (html: string): string => {
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || '';
    };

    const oldPlain = stripHtml(oldText);
    const newPlain = stripHtml(newText);

    if (oldPlain === newPlain) {
      return {
        oldHtml: oldText,
        newHtml: newText
      };
    }

    const oldWords = oldPlain.split(/\s+/).filter(w => w.length > 0);
    const newWords = newPlain.split(/\s+/).filter(w => w.length > 0);

    const dp: number[][] = Array(oldWords.length + 1).fill(null).map(() =>
      Array(newWords.length + 1).fill(0)
    );

    for (let i = 1; i <= oldWords.length; i++) {
      for (let j = 1; j <= newWords.length; j++) {
        if (oldWords[i - 1] === newWords[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }

    let i = oldWords.length;
    let j = newWords.length;
    const oldChanges: Set<number> = new Set();
    const newChanges: Set<number> = new Set();

    while (i > 0 || j > 0) {
      if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
        i--;
        j--;
      } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
        newChanges.add(j - 1);
        j--;
      } else if (i > 0) {
        oldChanges.add(i - 1);
        i--;
      }
    }

    const groupConsecutiveIndices = (indices: Set<number>): number[][] => {
      const sorted = Array.from(indices).sort((a, b) => a - b);
      const groups: number[][] = [];
      let currentGroup: number[] = [];

      for (const idx of sorted) {
        if (currentGroup.length === 0 || idx === currentGroup[currentGroup.length - 1] + 1) {
          currentGroup.push(idx);
        } else {
          groups.push(currentGroup);
          currentGroup = [idx];
        }
      }
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
      }

      return groups;
    };

    const highlightWordsInHtml = (html: string, plainText: string, changeGroups: number[][], isRemoval: boolean): string => {
      if (changeGroups.length === 0) {
        return html;
      }

      const words = plainText.split(/\s+/).filter(w => w.length > 0);
      const changeSet = new Set(changeGroups.flat());

      const changedWordGroups: string[][] = [];
      let currentGroup: string[] = [];

      for (let idx = 0; idx < words.length; idx++) {
        if (changeSet.has(idx)) {
          currentGroup.push(words[idx]);
        } else {
          if (currentGroup.length > 0) {
            changedWordGroups.push([...currentGroup]);
            currentGroup = [];
          }
        }
      }
      if (currentGroup.length > 0) {
        changedWordGroups.push(currentGroup);
      }

      let result = html;

      for (const group of changedWordGroups) {
        const phrase = group.join(' ');
        const escapedPhrase = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const highlightStyle = isRemoval
          ? 'bg-rs-red/20 text-rs-red px-2 py-1 rounded line-through'
          : 'bg-rs-green/20 text-rs-green px-2 py-1 rounded';

        const regex = new RegExp(`(?<=>)([^<]*?)(${escapedPhrase})([^<]*?)(?=<)`, 'g');
        result = result.replace(regex, (match, before, foundPhrase, after) => {
          return `${before}<span class="${highlightStyle}">${foundPhrase}</span>${after}`;
        });
      }

      return result;
    };

    const oldGroups = groupConsecutiveIndices(oldChanges);
    const newGroups = groupConsecutiveIndices(newChanges);

    const oldWithHighlight = oldChanges.size > 0
      ? highlightWordsInHtml(oldText, oldPlain, oldGroups, true)
      : oldText;

    const newWithHighlight = newChanges.size > 0
      ? highlightWordsInHtml(newText, newPlain, newGroups, false)
      : newText;

    return {
      oldHtml: oldWithHighlight,
      newHtml: newWithHighlight
    };
  };


  const findPublishedRecord = (pendingRecord: PendingChange): any | null => {
    if (changeType === 'forecast') {
      if (!pendingRecord.forecastQtrDateId) return null;

      const year = parseInt(pendingRecord.forecastQtrDateId.substring(0, 4));
      const month = parseInt(pendingRecord.forecastQtrDateId.substring(5, 7));
      const qtr = Math.ceil(month / 3);

      return publishedDataState.find(pub =>
        pub.forecastYear === year &&
        pub.forecastQtr === qtr &&
        pub.polymer === pendingRecord.polymer
      );
    } else if (changeType === 'drivers') {
      if (driversSubView === 'pricechanges') {
        return publishedDataState.find(pub =>
          pub.date === pendingRecord.date &&
          pub.driver === (pendingRecord as any).driver
        );
      } else {
        return publishedDataState.find(pub =>
          pub.date === pendingRecord.date &&
          pub.driverReport === pendingRecord.driverReport
        );
      }
    } else if (['peacc', 'ppacc', 'psacc', 'pvcacc'].includes(changeType)) {
      return publishedDataState.find(pub =>
        pub.date === pendingRecord.date
      );
    }
    return null;
  };

  const getChangedFields = (pendingRecord: PendingChange): FieldChange[] => {
    const publishedRecord = findPublishedRecord(pendingRecord);
    const changes: FieldChange[] = [];

    if (changeType === 'forecast') {
      const before = publishedRecord?.commentary || null;
      const after = pendingRecord.commentary || null;
      if (before !== after) {
        changes.push({
          field: 'commentary',
          label: 'Commentary',
          before,
          after
        });
      }
    } else if (changeType === 'drivers') {
      if (driversSubView === 'pricechanges') {
        const before = publishedRecord?.value || null;
        const after = (pendingRecord as any).value || null;
        if (before !== after) {
          changes.push({
            field: 'value',
            label: 'Price Change',
            before,
            after
          });
        }
      } else {
        const fields: Array<{key: keyof PendingChange, label: string}> = [
          { key: 'title', label: 'Title' },
          { key: 'marketOverviewBody', label: 'Market Overview' },
          { key: 'marketOverviewBodyTrial', label: 'Market Overview (Trial)' },
          { key: 'supplyDemandTitle', label: 'Supply Demand Title' },
          { key: 'supplyDemandBody', label: 'Supply Demand Body' },
          { key: 'feedstocksBody', label: 'Feedstocks' },
          { key: 'internationalBody', label: 'International' },
          { key: 'actionStrategies', label: 'Action Strategies' }
        ];

        fields.forEach(({ key, label }) => {
          const before = publishedRecord?.[key] || null;
          const after = pendingRecord[key] || null;
          if (before !== after) {
            changes.push({
              field: key as string,
              label,
              before,
              after
            });
          }
        });
      }
    } else if (changeType === 'peacc') {
      const fields = [
        { key: 'pe_inv_cum_gains_draws', label: 'PE Inventory Cum. Gains/Draws' },
        { key: 'pe_prod_sales', label: 'PE Production/Sales' },
        { key: 'hdpe', label: 'HDPE' },
        { key: 'ldpe', label: 'LDPE' },
        { key: 'lldpe', label: 'LLDPE' }
      ];

      fields.forEach(({ key, label }) => {
        const before = publishedRecord?.[key] || null;
        const after = (pendingRecord as any)[key] || null;
        if (after !== null && before !== after) {
          changes.push({ field: key, label, before, after });
        }
      });
    } else if (changeType === 'ppacc') {
      const before = publishedRecord?.pp_supply_demand || null;
      const after = (pendingRecord as any).pp_supply_demand || null;

      if (after !== null && before !== after) {
        changes.push({
          field: 'pp_supply_demand',
          label: 'PP Supply/Demand',
          before,
          after
        });
      }
    } else if (changeType === 'psacc') {
      const fields = [
        { key: 'ps_inv_cum_gains_draws', label: 'PS Inventory Cum. Gains/Draws' },
        { key: 'ps_supply_demand', label: 'PS Supply/Demand' },
        { key: 'ps_styrene_exports', label: 'PS Styrene Exports' },
        { key: 'ps_benzene_imports', label: 'PS Benzene Imports' },
        { key: 'ps_butadiene_imports', label: 'PS Butadiene Imports' }
      ];

      fields.forEach(({ key, label }) => {
        const before = publishedRecord?.[key] || null;
        const after = (pendingRecord as any)[key] || null;
        if (after !== null && before !== after) {
          changes.push({ field: key, label, before, after });
        }
      });
    } else if (changeType === 'pvcacc') {
      const fields = [
        { key: 'pvc_inv_cum_gains_draws', label: 'PVC Inventory Cum. Gains/Draws' },
        { key: 'pvc_prod_sales', label: 'PVC Production/Sales' },
        { key: 'pvc_exports', label: 'PVC Exports' },
        { key: 'pvc_domestic_sales', label: 'PVC Domestic Sales' }
      ];

      fields.forEach(({ key, label }) => {
        const before = publishedRecord?.[key] || null;
        const after = (pendingRecord as any)[key] || null;
        if (after !== null && before !== after) {
          changes.push({ field: key, label, before, after });
        }
      });
    }

    return changes;
  };

  const isNewRecord = (pendingRecord: PendingChange): boolean => {
    return findPublishedRecord(pendingRecord) === null;
  };

  const handleConfirmCancel = async () => {
    setLoading(true);
    setLoadingMessage('Discarding changes...');
    try {
      await onCancelStaging?.();
    } finally {
      setLoading(false);
      setCancelConfirmVisible(false);
    }
  };

  const handleCommit = async () => {
    setLoading(true);
    setLoadingMessage('Publishing changes...');
    setError(null);
    try {
      await onCommit();

      setLoadingMessage('Refreshing data...');

      if (changeType === 'daily') {
        // parent's onCommit handles reload
      } else if (changeType === 'forecast') {
        const updated = await apiClient.fetchForecastCommentary();
        setPublishedDataState(updated);
      } else if (changeType === 'drivers') {
        if (driversSubView === 'pricechanges') {
          const updated = await apiClient.fetchCachedDriverPriceChangesData();
          if (Array.isArray(updated) && updated.length > 0 && typeof updated[0] === 'object') {
            const firstItem = updated[0];
            const DRIVERS = ['PE', 'PP', 'PS', 'ABS', 'PC', 'PVC', 'PA66', 'PA6', 'PET'];
            const flattenedPublished: any[] = [];
            DRIVERS.forEach(driver => {
              const driverData = firstItem[driver as keyof typeof firstItem];
              if (Array.isArray(driverData)) {
                driverData.forEach((item: any) => {
                  flattenedPublished.push({
                    date: item.date,
                    driver: driver,
                    value: item.value
                  });
                });
              }
            });
            setPublishedDataState(flattenedPublished);
          }
        } else {
          const updated = [await apiClient.fetchDriverData()];
          setPublishedDataState(updated);
        }
      } else if (changeType === 'peacc') {
        const updated = commentaryType === 'final'
          ? await apiClient.fetchACCPEDataFinal()
          : await apiClient.fetchACCPEData();
        setPublishedDataState(updated);
      } else if (changeType === 'ppacc') {
        const updated = commentaryType === 'final'
          ? await apiClient.fetchACCPPDataFinal()
          : await apiClient.fetchACCPPData();
        setPublishedDataState(updated);
      } else if (changeType === 'psacc') {
        const updated = commentaryType === 'final'
          ? await apiClient.fetchACCPSDataFinal()
          : await apiClient.fetchACCPSData();
        setPublishedDataState(updated);
      } else if (changeType === 'pvcacc') {
        const updated = commentaryType === 'final'
          ? await apiClient.fetchACCPVCDataFinal()
          : await apiClient.fetchACCPVCData();
        setPublishedDataState(updated);
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to commit changes');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className={`bg-rs-black border border-rs-jet-light rounded-lg shadow-2xl overflow-hidden flex flex-col ${changeType === 'daily' ? 'w-full max-w-2xl max-h-[80vh]' : 'w-full h-full max-w-[98vw] max-h-[98vh]'}`}>
        {/* Header */}
        <div className="px-6 py-3 border-b border-rs-jet-light bg-rs-jet flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-rs-green">
                Preview Pending Changes
              </h2>
              <p className="text-sm text-rs-off-white/60 mt-1">
                Review changes before publishing to production
              </p>
            </div>
            {changeType !== 'daily' && (
              <button
                onClick={onClose}
                className="text-rs-off-white/70 hover:text-rs-off-white transition-colors"
              >
                <X size={24} />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {loading && pendingChanges.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="w-8 h-8 text-rs-green animate-spin" />
              <div className="text-rs-off-white/60">{loadingMessage}</div>
            </div>
          ) : error ? (
            <div className="bg-rs-red/20 border border-rs-red text-rs-red px-4 py-3 rounded">
              {error}
            </div>
          ) : pendingChanges.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-rs-off-white/60">No pending changes</div>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {pendingChanges.map((change, index) => {
                const changedFields = getChangedFields(change);
                const isNew = isNewRecord(change);

                return (
                  <div
                    key={index}
                    className="bg-rs-jet border border-rs-salmon/30 rounded-lg overflow-hidden"
                  >
                    {/* Record Header */}
                    <div className="bg-rs-jet-light px-4 py-3 border-b border-rs-jet-light">
                      <div className="flex items-start justify-between">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-3">
                            {changeType === 'daily' ? (
                              <>
                                <span className="text-rs-green font-semibold text-lg">Spot Feeds</span>
                                <span className="text-rs-off-white">
                                  {change.date && (() => {
                                    const [year, month, day] = change.date.split('T')[0].split('-');
                                    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
                                      .toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                                  })()}
                                </span>
                              </>
                            ) : changeType === 'forecast' ? (
                              <>
                                <span className="text-rs-green font-semibold text-lg">
                                  {change.polymer}
                                </span>
                                <span className="text-rs-off-white">
                                  Q{change.forecastQtr} {change.forecastYear || change.forecastQtrDateId?.split('-')[0]}
                                </span>
                              </>
                            ) : changeType === 'drivers' ? (
                              driversSubView === 'pricechanges' ? (
                                <>
                                  <span className="text-rs-green font-semibold text-lg">
                                    {(change as any).driver}
                                  </span>
                                  <span className="text-rs-off-white">
                                    {change.date && (() => {
                                      const [year, month, day] = change.date.split('T')[0].split('-');
                                      const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                      return localDate.toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      });
                                    })()}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className="text-rs-green font-semibold text-lg">
                                    {change.driverReport}
                                  </span>
                                  <span className="text-rs-off-white">
                                    {change.date}
                                  </span>
                                </>
                              )
                            ) : (
                              <>
                                <span className="text-rs-green font-semibold text-lg">
                                  {changeType.toUpperCase().replace('ACC', ' ACC')}
                                </span>
                                <span className="text-rs-off-white">
                                  {change.date && (() => {
                                    const [year, month, day] = change.date.split('T')[0].split('-');
                                    const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                    return localDate.toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    });
                                  })()}
                                </span>
                              </>
                            )}
                          </div>
                          {change.title && changeType === 'drivers' && driversSubView !== 'pricechanges' && (
                            <div className="text-sm text-rs-off-white/80">
                              {change.title}
                            </div>
                          )}
                          <div className="text-xs text-rs-off-white/60">
                            Updated by: {(change as any).last_updated_by || change.lastUpdatedBy || 'N/A'} •{' '}
                            {((change as any).last_updated_time || change.lastUpdatedTime)
                              ? new Date(asUtcTs((change as any).last_updated_time || change.lastUpdatedTime)).toLocaleString()
                              : 'N/A'}
                          </div>
                        </div>
                        {changeType !== 'daily' && (
                        <div>
                          {isNew ? (
                            <div className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded text-xs font-semibold flex items-center gap-2">
                              <AlertCircle size={14} />
                              NEW RECORD
                            </div>
                          ) : (
                            <div className="bg-rs-salmon/20 text-rs-salmon px-3 py-1 rounded text-xs font-semibold">
                              {changedFields.length} FIELD{changedFields.length !== 1 ? 'S' : ''} CHANGED
                            </div>
                          )}
                        </div>
                        )}
                      </div>
                    </div>

                    {/* Changed Fields */}
                    <div className="p-4 flex flex-col gap-4">
                      {changeType === 'daily' ? (
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <div className="text-xs font-semibold text-rs-green uppercase mb-2">Spot Prices</div>
                            <div className="flex flex-col gap-1">
                              {[
                                { key: 'spot_ethylene', label: 'Spot Ethylene' },
                                { key: 'spot_rgp', label: 'Spot RGP' },
                                { key: 'spot_pgp', label: 'Spot PGP' },
                                { key: 'spot_benzene', label: 'Spot Benzene' },
                                { key: 'spot_styrene', label: 'Spot Styrene' },
                                { key: 'spot_butadiene', label: 'Spot Butadiene' },
                                { key: 'vam', label: 'VAM' },
                              ].map(({ key, label }) => (
                                <div key={key} className="flex justify-between px-3 py-1.5 bg-rs-black rounded text-sm">
                                  <span className="text-rs-off-white/60">{label}</span>
                                  <span className="text-rs-off-white font-mono">
                                    {(change as any)[key] != null ? `$${(change as any)[key]}` : '—'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-semibold text-rs-off-white/60 uppercase mb-2">Contract Prices</div>
                            <div className="flex flex-col gap-1">
                              {[
                                { key: 'ethylene_contract', label: 'Ethylene Contract' },
                                { key: 'pgp_contract', label: 'PGP Contract' },
                                { key: 'benzene_contract', label: 'Benzene Contract' },
                                { key: 'styrene_contract', label: 'Styrene Contract' },
                                { key: 'butadiene_contract', label: 'Butadiene Contract' },
                              ].map(({ key, label }) => (
                                <div key={key} className="flex justify-between px-3 py-1.5 bg-rs-black rounded text-sm">
                                  <span className="text-rs-off-white/60">{label}</span>
                                  <span className="text-rs-off-white font-mono">
                                    {(change as any)[key] != null ? `$${(change as any)[key]}` : '—'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : isNew ? (
                        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-blue-400 mb-3">
                            <AlertCircle size={18} />
                            <span className="font-semibold">New Record - All Content is New</span>
                          </div>
                          {changeType === 'forecast' && change.commentary && (
                            <div>
                              <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                Commentary:
                              </div>
                              <div
                                className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-60 overflow-y-auto"
                                dangerouslySetInnerHTML={{ __html: change.commentary }}
                              />
                            </div>
                          )}
                          {changeType === 'drivers' && (
                            <div className="flex flex-col gap-3">
                              {change.marketOverviewBody && (
                                <div>
                                  <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                    Market Overview:
                                  </div>
                                  <div
                                    className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                    dangerouslySetInnerHTML={{ __html: change.marketOverviewBody }}
                                  />
                                </div>
                              )}
                              {change.supplyDemandBody && (
                                <div>
                                  <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                    {change.supplyDemandTitle || 'Supply Demand'}:
                                  </div>
                                  <div
                                    className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                    dangerouslySetInnerHTML={{ __html: change.supplyDemandBody }}
                                  />
                                </div>
                              )}
                              {change.feedstocksBody && (
                                <div>
                                  <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                    Feedstocks:
                                  </div>
                                  <div
                                    className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                    dangerouslySetInnerHTML={{ __html: change.feedstocksBody }}
                                  />
                                </div>
                              )}
                              {change.internationalBody && (
                                <div>
                                  <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                    International:
                                  </div>
                                  <div
                                    className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                    dangerouslySetInnerHTML={{ __html: change.internationalBody }}
                                  />
                                </div>
                              )}
                              {change.actionStrategies && (
                                <div>
                                  <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                    Action Strategies:
                                  </div>
                                  <div
                                    className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                    dangerouslySetInnerHTML={{ __html: change.actionStrategies }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                          {changeType === 'drivers' && driversSubView === 'pricechanges' && (
                            <div>
                              <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                Price Change Value:
                              </div>
                              <div className="text-rs-off-white text-lg font-semibold bg-rs-black border border-rs-jet-light rounded p-3">
                                {(change as any).value !== null && (change as any).value !== undefined ? (change as any).value : '(not set)'}
                              </div>
                            </div>
                          )}
                          {['peacc', 'ppacc', 'psacc', 'pvcacc'].includes(changeType) && (
                            <div className="flex flex-col gap-3">
                              {changeType === 'peacc' && (
                                <>
                                  {(change as any).pe_inv_cum_gains_draws && (
                                    <div>
                                      <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                        PE Inventory Cum. Gains/Draws:
                                      </div>
                                      <div
                                        className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: (change as any).pe_inv_cum_gains_draws }}
                                      />
                                    </div>
                                  )}
                                  {(change as any).pe_prod_sales && (
                                    <div>
                                      <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                        PE Production/Sales:
                                      </div>
                                      <div
                                        className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: (change as any).pe_prod_sales }}
                                      />
                                    </div>
                                  )}
                                  {(change as any).hdpe && (
                                    <div>
                                      <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                        HDPE:
                                      </div>
                                      <div
                                        className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: (change as any).hdpe }}
                                      />
                                    </div>
                                  )}
                                  {(change as any).ldpe && (
                                    <div>
                                      <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                        LDPE:
                                      </div>
                                      <div
                                        className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: (change as any).ldpe }}
                                      />
                                    </div>
                                  )}
                                  {(change as any).lldpe && (
                                    <div>
                                      <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                        LLDPE:
                                      </div>
                                      <div
                                        className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: (change as any).lldpe }}
                                      />
                                    </div>
                                  )}
                                </>
                              )}
                              {changeType === 'ppacc' && (change as any).pp_supply_demand && (
                                <div>
                                  <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                    PP Supply/Demand:
                                  </div>
                                  <div
                                    className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                    dangerouslySetInnerHTML={{ __html: (change as any).pp_supply_demand }}
                                  />
                                </div>
                              )}
                              {changeType === 'psacc' && (
                                <>
                                  {(change as any).ps_inv_cum_gains_draws && (
                                    <div>
                                      <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                        PS Inventory Cum. Gains/Draws:
                                      </div>
                                      <div
                                        className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: (change as any).ps_inv_cum_gains_draws }}
                                      />
                                    </div>
                                  )}
                                  {(change as any).ps_supply_demand && (
                                    <div>
                                      <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                        PS Supply/Demand:
                                      </div>
                                      <div
                                        className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: (change as any).ps_supply_demand }}
                                      />
                                    </div>
                                  )}
                                  {(change as any).ps_styrene_exports && (
                                    <div>
                                      <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                        PS Styrene Exports:
                                      </div>
                                      <div
                                        className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: (change as any).ps_styrene_exports }}
                                      />
                                    </div>
                                  )}
                                  {(change as any).ps_benzene_imports && (
                                    <div>
                                      <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                        PS Benzene Imports:
                                      </div>
                                      <div
                                        className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: (change as any).ps_benzene_imports }}
                                      />
                                    </div>
                                  )}
                                  {(change as any).ps_butadiene_imports && (
                                    <div>
                                      <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                        PS Butadiene Imports:
                                      </div>
                                      <div
                                        className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: (change as any).ps_butadiene_imports }}
                                      />
                                    </div>
                                  )}
                                </>
                              )}
                              {changeType === 'pvcacc' && (
                                <>
                                  {(change as any).pvc_inv_cum_gains_draws && (
                                    <div>
                                      <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                        PVC Inventory Cum. Gains/Draws:
                                      </div>
                                      <div
                                        className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: (change as any).pvc_inv_cum_gains_draws }}
                                      />
                                    </div>
                                  )}
                                  {(change as any).pvc_prod_sales && (
                                    <div>
                                      <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                        PVC Production/Sales:
                                      </div>
                                      <div
                                        className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: (change as any).pvc_prod_sales }}
                                      />
                                    </div>
                                  )}
                                  {(change as any).pvc_exports && (
                                    <div>
                                      <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                        PVC Exports:
                                      </div>
                                      <div
                                        className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: (change as any).pvc_exports }}
                                      />
                                    </div>
                                  )}
                                  {(change as any).pvc_domestic_sales && (
                                    <div>
                                      <div className="text-xs text-rs-off-white/60 mb-2 font-semibold uppercase">
                                        PVC Domestic Sales:
                                      </div>
                                      <div
                                        className="text-rs-off-white text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-96 overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: (change as any).pvc_domestic_sales }}
                                      />
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ) : changedFields.length > 0 ? (
                        changedFields.map((fieldChange, fieldIndex) => {
                          const diff = computeTextDiff(
                            typeof fieldChange.before === 'string' ? fieldChange.before : null,
                            typeof fieldChange.after === 'string' ? fieldChange.after : null
                          );

                          return (
                          <div
                            key={fieldIndex}
                            className="border border-rs-salmon/30 rounded-lg overflow-hidden"
                          >
                            <div className="bg-rs-salmon/10 px-3 py-2 border-b border-rs-salmon/30">
                              <span className="text-xs font-semibold text-rs-salmon uppercase">
                                {fieldChange.label}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 divide-x divide-rs-jet-light">
                              {/* Before */}
                              <div className="p-3">
                                <div className="text-xs text-rs-off-white/60 mb-2 font-semibold flex items-center gap-2">
                                  <span className="px-2 py-0.5 bg-rs-red/20 text-rs-red rounded">BEFORE</span>
                                  <span>Current Production</span>
                                </div>
                                {fieldChange.before ? (
                                  fieldChange.field === 'title' || fieldChange.field === 'supplyDemandTitle' ? (
                                    <div className="text-rs-off-white/80 text-sm">
                                      {fieldChange.before}
                                    </div>
                                  ) : (
                                    <div
                                      className="text-rs-off-white/80 text-sm bg-rs-black border border-rs-jet-light rounded p-3 max-h-[60vh] overflow-y-auto"
                                      dangerouslySetInnerHTML={{ __html: diff.oldHtml }}
                                    />
                                  )
                                ) : (
                                  <div className="text-rs-off-white/40 text-sm italic">
                                    (empty)
                                  </div>
                                )}
                              </div>

                              {/* After */}
                              <div className="p-3">
                                <div className="text-xs text-rs-off-white/60 mb-2 font-semibold flex items-center gap-2">
                                  <span className="px-2 py-0.5 bg-rs-green/20 text-rs-green rounded">AFTER</span>
                                  <span>Pending Change</span>
                                  <ChevronRight size={14} className="text-rs-green" />
                                </div>
                                {fieldChange.after ? (
                                  fieldChange.field === 'title' || fieldChange.field === 'supplyDemandTitle' ? (
                                    <div className="text-rs-off-white text-sm font-medium">
                                      {fieldChange.after}
                                    </div>
                                  ) : (
                                    <div
                                      className="text-rs-off-white text-sm bg-rs-black border border-rs-green/30 rounded p-3 max-h-[60vh] overflow-y-auto"
                                      dangerouslySetInnerHTML={{ __html: diff.newHtml }}
                                    />
                                  )
                                ) : (
                                  <div className="text-rs-off-white/40 text-sm italic">
                                    (empty)
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                        })
                      ) : (
                        <div className="text-rs-off-white/60 text-sm italic text-center py-4">
                          No changes detected
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-rs-jet-light bg-rs-jet flex-shrink-0">
          {changeType === 'daily' ? (
            cancelConfirmVisible ? (
              <div className="flex flex-col gap-3">
                <p className="text-rs-off-white/80 text-sm">
                  This will permanently discard your staged changes. This cannot be undone.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setCancelConfirmVisible(false)}
                    disabled={loading}
                    className="px-5 py-2 bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white rounded transition-colors disabled:opacity-50"
                  >
                    Keep Changes
                  </button>
                  <button
                    onClick={handleConfirmCancel}
                    disabled={loading}
                    className="px-5 py-2 bg-rs-red hover:bg-rs-red/80 text-rs-off-white font-semibold rounded transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? 'Discarding…' : 'Yes, Discard Changes'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-center">
                <button
                  onClick={onEdit}
                  disabled={loading}
                  className="px-5 py-2 bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white rounded transition-colors disabled:opacity-50"
                >
                  Edit
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => setCancelConfirmVisible(true)}
                    disabled={loading}
                    className="px-5 py-2 bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-red rounded transition-colors disabled:opacity-50"
                  >
                    Cancel Changes
                  </button>
                  <button
                    onClick={handleCommit}
                    disabled={loading || pendingChanges.length === 0}
                    className="px-6 py-2 bg-rs-green hover:bg-rs-dark-green !text-rs-black font-semibold rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? loadingMessage.replace('...', '') : 'Publish Changes'}
                  </button>
                </div>
              </div>
            )
          ) : (
            <div className="flex justify-between items-center">
              <div className="text-sm text-rs-off-white/60">
                {pendingChanges.length} change{pendingChanges.length !== 1 ? 's' : ''} pending
              </div>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  disabled={loading}
                  className="px-6 py-2 bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCommit}
                  disabled={loading || pendingChanges.length === 0}
                  className="px-6 py-2 bg-rs-green hover:bg-rs-dark-green !text-rs-black font-semibold rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {loading ? loadingMessage.replace('...', '') : 'Publish Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingChangesModal;

